import { HeapAction, HeapMetadata } from 'clientServerShared/types/eventTracking';
import { Link, LinkProps } from '@mui/material';
import { MouseEvent, ReactNode, forwardRef, useState } from 'react';

import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import GetAppIcon from '@mui/icons-material/GetApp';
import { RenderIf } from 'projects/sharedComponents/src/renderStates/renderIf';
import { ShareButtonFragment } from 'projects/sharedComponents/src/generated/graphql-client-types';
import { copyLinkButtonOnClickHandler } from 'projects/sharedComponents/src/services/eventHandlers/copyLinkButtons';
import { getHeapDataAttributes } from 'projects/sharedComponents/src/services/eventTracking/eventTracking';

type ShareLinkBaseProps = {
  children?: ReactNode;
  heapMetadata?: Omit<HeapMetadata, 'heapAction'>;
};

type EmailShareLinkProps = LinkProps<
  'a',
  ShareLinkBaseProps & {
    href: ShareButtonFragment['emailMailTo'];
  }
>;
export const EmailShareLink = forwardRef<HTMLAnchorElement, EmailShareLinkProps>(
  ({ children, href, heapMetadata = {}, ...restProps }, ref) => {
    const hasChildren = !!children;

    return (
      <Link
        {...restProps}
        ref={ref}
        href={href}
        {...getHeapDataAttributes({
          heapAction: HeapAction.SHARE_RESULTS_EMAIL,
          ...heapMetadata,
        })}
      >
        <RenderIf condition={hasChildren}>{children}</RenderIf>
        <RenderIf condition={!hasChildren}>
          <EmailIcon />
          EMAIL
        </RenderIf>
      </Link>
    );
  }
);

type FacebookShareLinkProps = LinkProps<
  'a',
  ShareLinkBaseProps & {
    href: ShareButtonFragment['facebookUrl'];
  }
>;
export const FacebookShareLink = forwardRef<HTMLAnchorElement, FacebookShareLinkProps>(
  ({ children, href, heapMetadata = {}, ...restProps }, ref) => {
    const hasChildren = !!children;
    return (
      <Link
        {...restProps}
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener"
        {...getHeapDataAttributes({
          ...heapMetadata,
          heapAction: HeapAction.SHARE_RESULTS_FACEBOOK,
        })}
      >
        <RenderIf condition={!hasChildren}>
          <FacebookIcon />
          FACEBOOK
        </RenderIf>
        <RenderIf condition={hasChildren}>{children}</RenderIf>
      </Link>
    );
  }
);

type DownloadShareLinkProps = LinkProps<
  'a',
  ShareLinkBaseProps & {
    href: ShareButtonFragment['downloadUrl'];
    shareItemId: ShareButtonFragment['shareItemId'];
    download?: never;
  }
>;
export const DownloadShareLink = forwardRef<HTMLAnchorElement, DownloadShareLinkProps>(
  ({ children, href, shareItemId, heapMetadata = {}, ...restProps }, ref) => {
    const hasChildren = !!children;
    return (
      <Link
        {...restProps}
        ref={ref}
        href={href ?? undefined}
        download={shareItemId}
        {...getHeapDataAttributes({
          ...heapMetadata,
          heapAction: HeapAction.SHARE_RESULTS_DOWNLOAD,
        })}
      >
        <RenderIf condition={hasChildren}>{children}</RenderIf>
        <RenderIf condition={!hasChildren}>
          <GetAppIcon />
          DOWNLOAD
        </RenderIf>
      </Link>
    );
  }
);

type CopyShareUrlProps = LinkProps<
  'a',
  ShareLinkBaseProps & {
    href: ShareButtonFragment['copyLinkUrl'];
    onClick?: never;
  }
>;

export const CopyShareUrl = forwardRef<HTMLAnchorElement, CopyShareUrlProps>(
  ({ children, href, heapMetadata = {}, ...restProps }, ref) => {
    const [copyLinkText, setCopyLinkText] = useState('COPY LINK');
    const hasChildren = !!children;
    const copyLink = async (event: MouseEvent<HTMLAnchorElement>): Promise<void> => {
      event.preventDefault();
      await copyLinkButtonOnClickHandler(event);
      setCopyLinkText('LINK COPIED');
      setTimeout(() => {
        setCopyLinkText('COPY LINK');
      }, 3000);
    };
    return (
      <Link
        {...restProps}
        ref={ref}
        href={href}
        onClick={copyLink}
        {...getHeapDataAttributes({
          ...heapMetadata,
          heapAction: HeapAction.SHARE_RESULTS_COPY_LINK,
        })}
      >
        <RenderIf condition={hasChildren}>{children}</RenderIf>
        <RenderIf condition={!hasChildren}>
          <GetAppIcon />
          {copyLinkText}
        </RenderIf>
      </Link>
    );
  }
);
